import { Close } from '@mui/icons-material'
import { Modal } from "@mui/material"
import { Link } from "react-router-dom";
import * as React from "react"
import { Button, Col, Container, Row } from 'react-bootstrap'
import { useTranslation } from "react-i18next"

import icon_frontend from "./../../images/frontend.svg";
import icon_backend from "./../../images/backend.svg";
import icon_embedded from "./../../images/embedded.svg";
import icon_mobile from "./../../images/mobile.svg";
import icon_pc from "./../../images/pc.svg";
import icon_brain from "./../../images/brain.svg";
import icon_cloud from "./../../images/cloud.svg";
import icon_trend from "./../../images/trend.svg";

const technologies = [
  {
    technology: "FRONTEND",
    img: icon_frontend,
    numberOfLabels: 3,
    numberOfKeywords: 3,
  },
  {
    technology: "WEB_SYSTEM",
    img: icon_backend,
    numberOfLabels: 2,
    numberOfKeywords: 4,
  },
  {
    technology: "EMBEDDED_SYSTEM",
    img: icon_embedded,
    numberOfLabels: 3,
    numberOfKeywords: 2,
  },
  {
    technology: "MOBILE_APP",
    img: icon_mobile,
    numberOfLabels: 2,
    numberOfKeywords: 4,
  },
  {
    technology: "DESKTOP_APP",
    img: icon_pc,
    numberOfLabels: 2,
    numberOfKeywords: 2,
  },
  {
    technology: "AI",
    img: icon_brain,
    numberOfLabels: 2,
    numberOfKeywords: 3,
  },
  {
    technology: "CLOUD",
    img: icon_cloud,
    numberOfLabels: 2,
    numberOfKeywords: 6,
  },
  {
    technology: "OTHERS",
    img: icon_trend,
    numberOfLabels: 2,
    numberOfKeywords: 1,
  }
]

const width = "185px"

type SelectedCardProps = (typeof technologies[number]) & {onClose: () => void};

const SelectedCard: React.FC<SelectedCardProps> = ({technology, img, numberOfKeywords, onClose}) => {
  const { t } = useTranslation("translations", {keyPrefix: "INDEX.TECHNOLOGY"})

  return (
    <Modal className="h-100 d-flex align-items-center justify-content-center" open={true} onClose={onClose}>
      <Container className="animation-slide-in text-center m-3 p-4 bg-light">
        <Row>
          <Col xs="12" className="text-end mb-2">
            <span className="cursor-pointer" onClick={onClose}>Close<Close className="ms-2" /></span>
          </Col>
          <Col md="4">
            <img src={img} alt="technology icon" style={{maxWidth: "200px"}} className="w-100 mb-2" />
          </Col>
          <Col md="8" className="text-start">
            <h4>{t(`${technology}.TITLE`)}</h4>
            <p className="mx-3">{t(`${technology}.DESC`)}</p>
            <small className="p-3">- keywords -</small>
            <div className="mx-3 d-flex align-items-center flex-wrap text-center">
              {Array.from({ length: numberOfKeywords }, (_, i) => i + 1).map((i) => (
                <p className="rounded-pill bg-dark text-white mx-1 px-3 py-1 d-flex justify-content-center align-items-center" key={i}>
                  <small>
                    {t(`${technology}.KEYWORD_${i}`)}
                  </small>
                </p>
              ))}
            </div>
          </Col>
        </Row>
      </Container>
    </Modal>
  )
}

type CardProps = (typeof technologies[number]) & {onSelectedCard(): void}

const Card: React.FC<CardProps> = ({technology, img, numberOfLabels, onSelectedCard}) => {
  const { t } = useTranslation("translations", {keyPrefix: "INDEX.TECHNOLOGY"})

  return (
    <div style={{maxWidth: width, minWidth: width, width}} className={`animation-on-cursor cursor-pointer text-center mb-2 shadow mx-2 my-2 rounded`} onClick={onSelectedCard}>
      <div className="p-2 h-100 d-flex flex-column">
        <img src={img} alt="technology icon" className="w-100 px-2 mb-2" />
        <h6 className="fw-bold">{t(`${technology}.TITLE`)}</h6>
        <div className="mb-3">
          {Array.from({ length: numberOfLabels }, (_, i) => i + 1).map((i) => 
            <p className="m-0 h5" key={i}><small>{t(`${technology}.LABEL_${i}`)}</small></p>
          )}
        </div>
        <small className="text-primary mt-auto mb-3">{t("CARD_DETAIL")} &gt;</small>
      </div>
    </div>
  )
}

export const Technology: React.FC = () => {
  const [selectedTechnology, setSelectedTechnology] = React.useState<null | typeof technologies[number]>(null)
  const { t } = useTranslation("translations", {keyPrefix: "INDEX.TECHNOLOGY"})

  return (
    <>
      <Container>
        <article className="pt-5 py-4">
          <div className="w-100 text-center">
            <h1 className="mb-0">TECHNOLOGY</h1>
            <small>{t("SUBTITLE")}</small>
            <p className="mb-1 h6 mt-3">{t("DESC_1")}</p>
            <p className="mb-2 h6">{t("DESC_2")}</p>
          </div>
        </article>
      </Container>
      <div className="d-flex flex-wrap justify-content-center">
        {selectedTechnology && (
          <SelectedCard {...selectedTechnology} onClose={() => setSelectedTechnology(null)} />
        )}
        {technologies.map((technology) => (
          <Card {...technology} onSelectedCard={() => setSelectedTechnology(technology)} key={technology.technology} />
        ))}
      </div>
      <Container>
        <div className="row my-5">
          <Link to="/works" className="d-block col-md-4 offset-md-2">
            <Button variant="dark" className="w-100 rounded-0 mb-3">
              {t("BUTTON_WORKS")}
            </Button>
          </Link>
          <Link to="/#contact" className="d-block col-md-4">
            <Button variant="success" className="w-100 rounded-0 text-light">
              {t("BUTTON_CONTACT")}
            </Button>
          </Link>
        </div>
      </Container>
    </>
  )
}